import React, { useMemo } from 'react'

import { formatMoney } from 'utils'

// TODO Replace with the real data type from Orval
export interface BudgetData {
  monthlyAllowance: number;
  totalMonthlyPremiums: number;
  offThatchPremiums: number;
  onThatchPremiums: number;
  qmeEnabled: boolean;
}

interface MemberBudgetCardProps {
  budgetData: BudgetData;
  isActive: boolean;
  numberOfPlans: number;
  sideBySide?: boolean;
}

export interface LineItem {
  circleColor?: string;
  name: string;
  amount: number;
}

export interface BarSection {
  color?: string;
  percentage: number;
}

/**
 * Assigns circle color CSS classes to line items for visual representation.
 * Mutates the original lineItems array by adding a circleColor property to each item.
 * "Unused" items get gray color, others get progressive Thatch brand colors.
 *
 * @param lineItems - Array of line items to assign colors to (mutated in place)
 */
export const setLineItemCircleColor = (lineItems: LineItem[]): void => {
  const thatchColorClasses = {
    300: 'bg-thatch-300',
    500: 'bg-thatch-500',
    700: 'bg-[#5C1C9B]' // fallback to arbitrary value since bg-thatch-700 doesn't exist
  }
  let colorWeight = 300

  lineItems.forEach((item) => {
    if (item.name === 'Unused') {
      item.circleColor = 'bg-gray-300'
    } else {
      item.circleColor = thatchColorClasses[colorWeight as keyof typeof thatchColorClasses]
      colorWeight += 200
    }
  })
}

/**
 * Converts line items into bar sections for chart visualization.
 * Calculates the percentage each line item represents of the total amount
 * and creates BarSection objects with color and percentage data.
 *
 * @param lineItems - Array of line items with amounts and colors
 * @param total - Total amount to calculate percentages against
 * @returns Array of bar sections with color and percentage properties
 */
export const makeBarSectionsFromLineItems = (lineItems: LineItem[], total: number): BarSection[] => {
  return lineItems.map((item) => ({
    color: item.circleColor,
    percentage: (item.amount / total) * 100
  }))
}

/**
 * Generates line items for budget breakdown display based on various financial scenarios.
 * Handles cases where allowance covers costs vs. requires paycheck deductions,
 * on-Thatch vs. off-Thatch premiums, and QME (Qualified Medical Expenses) settings.
 *
 * @param budgetData - Budget information including allowances, premiums, and QME status
 * @param insuranceCostLabel - Label to use for insurance costs (varies by number of plans)
 * @returns Object containing array of line items and whether a deduction/QME applies
 */
export const getLineItems = (budgetData: BudgetData, insuranceCostLabel: string): {
  lineItems: LineItem[],
  hasDeductionOrQme: boolean
} => {
  const { monthlyAllowance, totalMonthlyPremiums, offThatchPremiums, onThatchPremiums, qmeEnabled } = budgetData

  const allowanceLessThanCost = monthlyAllowance < totalMonthlyPremiums
  const someAmountOffThatch = offThatchPremiums > 0

  const lineItems: LineItem[] = []
  let hasDeductionOrQme = true

  if (allowanceLessThanCost) {
    if (someAmountOffThatch) {
      if (onThatchPremiums < monthlyAllowance) {
        lineItems.push({ name: 'Paid by Thatch', amount: onThatchPremiums })
        lineItems.push({ name: 'Paid by you & able to be reimbursed', amount: monthlyAllowance - onThatchPremiums })
      } else {
        lineItems.push({ name: 'Paid by Thatch', amount: monthlyAllowance })
      }
    } else {
      lineItems.push({ name: 'From your employer', amount: monthlyAllowance })
    }

    lineItems.push({ name: 'From your paychecks', amount: totalMonthlyPremiums - monthlyAllowance })
  } else {
    if (someAmountOffThatch) {
      lineItems.push({ name: 'Insurance costs paid by Thatch', amount: onThatchPremiums })
      lineItems.push({ name: 'Insurance costs paid by you & able to be reimbursed', amount: offThatchPremiums })
    } else {
      lineItems.push({ name: insuranceCostLabel, amount: totalMonthlyPremiums })
    }

    const difference = Number(Math.abs(monthlyAllowance - totalMonthlyPremiums).toFixed(2))
    if (difference > 0) {
      const label = qmeEnabled ? 'Remainder added to your balance' : 'Unused'
      lineItems.push({ name: label, amount: difference })
    }
    hasDeductionOrQme = qmeEnabled && difference > 0
  }

  // Filter out line items with 0 amount
  const filteredLineItems = lineItems.filter(item => item.amount > 0)

  setLineItemCircleColor(filteredLineItems)

  return { lineItems: filteredLineItems, hasDeductionOrQme }
}

/**
 * Determines the appropriate title and total amount to display in the budget card.
 * When allowance covers costs, shows "Monthly allowance" with allowance amount.
 * When costs exceed allowance, shows insurance cost label with premium amount.
 *
 * @param monthlyAllowance - The monthly allowance amount from employer
 * @param totalMonthlyPremiums - Total monthly insurance premium costs
 * @param numberOfPlans - Number of insurance plans (affects labeling)
 * @returns Object with title, total amount, and insurance cost label
 */
export const getTitleAndTotal = (monthlyAllowance: number, totalMonthlyPremiums: number, numberOfPlans: number) => {
  const allowanceLessThanCost = monthlyAllowance < totalMonthlyPremiums
  const insuranceCostLabel = numberOfPlans === 1 ? 'Insurance cost' : 'Total insurance cost'

  const title = allowanceLessThanCost ? insuranceCostLabel : 'Monthly allowance'
  const total = allowanceLessThanCost ? totalMonthlyPremiums : monthlyAllowance

  return { title, total, insuranceCostLabel }
}

const MemberBudgetCard: React.FC<MemberBudgetCardProps> = (
  {
    budgetData,
    isActive = true,
    numberOfPlans,
    sideBySide = false
  }
) => {
  const { title, total, lineItems, barSections, hasDeductionOrQme } = useMemo(() => {
    const { title, total, insuranceCostLabel } = getTitleAndTotal(budgetData.monthlyAllowance, budgetData.totalMonthlyPremiums, numberOfPlans)
    const { lineItems, hasDeductionOrQme } = getLineItems(budgetData, insuranceCostLabel)
    const barSections = makeBarSectionsFromLineItems(lineItems, total)
    return { title, total, lineItems, barSections, hasDeductionOrQme }
  }, [budgetData, numberOfPlans])

  const renderSideBySide = sideBySide && !hasDeductionOrQme

  const bgColor = isActive ? 'bg-white' : 'bg-gray-100'

  const containerClasses = renderSideBySide ? 'flex-row' : 'flex-col gap-2'

  const titleAndTotal = (
    <div className='flex flex-row justify-between items-start gap-2'>
      <div className='body text-main'>{title}</div>
      <div className='body-bold text-main'>{formatMoney(total)}</div>
    </div>
  )

  const bar = (
    <div className='flex flex-row items-center gap-1'>
      {barSections.map((section, index) => (
        <div
          key={index}
          className={`h-4 rounded ${section.color}`}
          style={{
            flex: `${section.percentage} 1 0%`
          }}
        />
      ))}
    </div>
  )

  return (
    <div className={`h-full rounded-2xl border border-gray-200 shadow-sm p-4 ${bgColor}`}>
      <div className={`flex w-full ${containerClasses}`}>
        {!renderSideBySide &&
          <>
            {titleAndTotal}
            {bar}
          </>}
        {renderSideBySide &&
          <>
            <div className='flex flex-col flex-1 gap-4'>
              {titleAndTotal}
              {bar}
            </div>
            <div className='w-[1px] bg-gray-200 self-stretch mx-6' />
          </>}
        <div className='flex flex-col flex-1 gap-1 justify-center'>
          {lineItems.map((item, index) => (
            <div key={index} className='flex flex-row justify-between items-start gap-4'>
              <div className='flex flex-row items-center gap-2'>
                <div className={`w-2 h-2 rounded-full ${item.circleColor}`} />
                <div className='body-sm text-gray-500'>{item.name}</div>
              </div>
              <div className='body-sm text-main'>{formatMoney(item.amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default MemberBudgetCard
